import { APIErrors } from '../../../../api-client/enums'
import { FormFields } from '../enums'
import not from '../../../../utils/not'
import isEmailValid from '../../../../utils/is-email-valid'
import isPhoneNumbersValid from '../../../../utils/is-phone-number-valid'

// ----------------------------------------------------------------------

/**
 * @param {string} field Name of the field
 * @returns {boolean}
 */
function isPhoneNumberField(field) {
  return field === FormFields.PhoneNumber
}

/**
 * @param {string} field Name of the field
 * @returns {boolean}
 */
function isEmailField(field) {
  return field === FormFields.Email
}

/**
 * @param {object} errors Errors object
 * @param {string} field Field Name
 * @returns {boolean}
 */
function isErrorExists(errors, field) {
  return Boolean(errors[field])
}

// ----------------------------------------------------------------------

export const validationErrors = {
  required: 'Required',
  email: 'Email is not valid',
  phoneNumber: 'Mobile Phone number must be 10 digits long',
  zipCode: 'Please enter a valid postal code',
}

/**
 * Required fields
 * @type {{[p: string]: boolean}}
 */
export const requiredFields = {
  [FormFields.FirstName]: true,
  [FormFields.LastName]: true,
  [FormFields.Email]: true,
  [FormFields.Address]: true,
  [FormFields.State]: true,
  [FormFields.City]: true,
  [FormFields.ZipCode]: true,
  [FormFields.DealershipName]: true,
  [FormFields.PhoneNumber]: true,
}

/**
 * @param {string} field - Field Name
 * @returns {boolean}
 */
export const isFieldRequired = (field) => requiredFields[field]

/**
 * @param {string} value - Field Value
 * @returns {boolean}
 */
export const isFieldEmpty = (value) => value === ''

/**
 * Validates the form
 * @param {State} state
 * @returns {{isValid: boolean, errors: {[p: string]: boolean}}}
 */
export function validate(state) {
  const errors = {}
  let isValid = true

  for (const field of Object.keys(state)) {
    if (isFieldRequired(field) && isFieldEmpty(state[field])) {
      isValid = false
      errors[field] = validationErrors.required
    }

    if (
      isEmailField(field) &&
      not(isEmailValid)(state[field]) &&
      not(isErrorExists)(errors, field)
    ) {
      isValid = false
      errors[field] = validationErrors.email
    }

    if (
      isPhoneNumberField(field) &&
      not(isFieldEmpty)(state[field]) &&
      not(isPhoneNumbersValid)(state[field]) &&
      not(isErrorExists)(errors, field)
    ) {
      isValid = false
      errors[field] = validationErrors.phoneNumber
    }
  }

  return { isValid, errors }
}

/**
 * Server Errors mapper
 * @param errors
 * @returns {{}|{general: string}}
 */
export function mapServerErrors(errors) {
  if (!errors) return {}

  const mappedErrors = {}

  errors.forEach((error) => {
    if (!error) {
      return {}
    }

    let code = parseInt(error.code)
    if (isNaN(code)) {
      code = error.code
    }
    switch (code) {
      case APIErrors.EmailAlreadyTaken:
        mappedErrors[FormFields.Email] = error.message
        break
      case APIErrors.PhoneNumberNotValid:
        mappedErrors[FormFields.PhoneNumber] = error.message
        break
      case APIErrors.InvalidPhoneNumberFormat:
        mappedErrors[FormFields.PhoneNumber] = error.message
        break
      case APIErrors.ZipCodeDoesNotExists:
        mappedErrors[FormFields.ZipCode] = validationErrors.zipCode
        break
      case APIErrors.InvalidZipCode:
        mappedErrors[FormFields.ZipCode] = validationErrors.zipCode
        break
      case APIErrors.InvalidDealershipZipCode:
        mappedErrors[FormFields.ZipCode] = validationErrors.zipCode
        break
      case APIErrors.TimezoneNotFound:
        mappedErrors[FormFields.ZipCode] = validationErrors.zipCode
        break
      case APIErrors.TimezoneNotSupported:
        mappedErrors[FormFields.ZipCode] = validationErrors.zipCode
        break
      case APIErrors.InvalidGoogleApiResponseStatusCode:
        mappedErrors[FormFields.ZipCode] = validationErrors.zipCode
        break
      default:
        mappedErrors['general'] = 'Something went wrong. Please try again'
        break
    }
  })

  return mappedErrors
}
